import React, { useState, useEffect } from "react";
import { getLoggedInUser, logout } from "../services/authService";
import { validateUserRole, fetchUserMspId } from "../utils/authUtils";

interface RouteGuardProps {
  children: React.ReactNode;
}

export default function RouteGuard({ children }: Readonly<RouteGuardProps>) {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [userRole, setUserRole] = useState<string>("");
  const [mspId, setMspId] = useState<number | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    checkAuthorization();
  }, []);

  const checkAuthorization = async () => {
    try {
      const user = await getLoggedInUser();

      if (!user) {
        await logout();
        return;
      }

      const { customRole } = user;

      // VALIDATE ROLE
      if (!validateUserRole(customRole)) {
        setIsAuthorized(false);
        setAuthChecked(true);
        return;
      }

      setUserRole(customRole || "");

      // IF USER IS MSP, FETCH THEIR MSP ID
      let userMspId: number | null = null;
      if (customRole === "msp") {
        userMspId = await fetchUserMspId();
        if (!userMspId || userMspId === 0) {
          setIsAuthorized(false);
          setAuthChecked(true);
          return;
        }
        setMspId(userMspId);
      }

      setIsAuthorized(true);
      setAuthChecked(true);
    } catch (error) {
      console.error("Authorization check failed:", error);
      setIsAuthorized(false);
      setAuthChecked(true);
    }
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white p-4">Checking authorization...</div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-gray-300 mb-6">
            You don't have permission to access this application. Please contact
            your administrator.
          </p>
          <button
            onClick={logout}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors "
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  // Pass props to children
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<any>, {
        role: userRole,
        mspId: mspId,
      });
    }
    return child;
  });

  return <>{childrenWithProps}</>;
}
